import { Fragment, useState } from 'react';
import { Sparkline } from '../../charts/Sparkline';
import { ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';

/**
 * One client's health score, and what it is made of — the client's Health
 * tab (GET /am/clients/:id/health, routes/amHealth.ts).
 *
 * The score is worked out every night from sixteen measures in five groups
 * (api/src/lib/amHealth.ts). This tab lists every one of them, scored or not,
 * so "why is this client red" is answered on the page: the figure, what it
 * scored, the share of the total it carried, and why. A measure with nothing
 * to judge says so and carries no weight — the others take its share.
 *
 * An override is for what the data cannot see. It needs a reason and lapses
 * after 90 days, so a forgotten one does not hide a client for good.
 */

type GroupKey = 'financial' | 'engagement' | 'service' | 'technical' | 'contract';

interface MeasureResult {
  key: string;
  group: GroupKey;
  label: string;
  how: string;
  source: string;
  value: number | null;
  display: string;
  score: number | null;
  weight: number;
  why: string;
}

interface Health {
  score: number | null;
  computed: number | null;
  computedUtc: string | null;
  breakdown: {
    computed: number | null;
    computedUtc: string;
    measured: number;
    groups: { key: GroupKey; label: string; weight: number; score: number | null }[];
    measures: MeasureResult[];
    unavailable: string[];
  } | null;
  override: { score: number; reason: string | null; by: string | null; until: string | null } | null;
  history: { date: string; score: number | null; computed: number | null }[];
  groups: { key: GroupKey; label: string; weight: number }[];
  measures: { key: string; group: GroupKey; label: string; how: string; source: string }[];
  overrideDays: number;
}

export function healthBand(score: number | null): { label: string; badge: string } {
  if (score === null) return { label: 'Not scored', badge: 'badge-neutral' };
  if (score >= 80) return { label: 'Healthy', badge: 'badge-good' };
  if (score >= 60) return { label: 'Needs attention', badge: 'badge-warning' };
  return { label: 'At risk', badge: 'badge-critical' };
}

function ScoreBadge({ score }: { score: number | null }) {
  const band = healthBand(score);
  return <span className={`badge ${band.badge}`}>{score === null ? '—' : score}</span>;
}

/** Consecutive days, ending today, the shown score has been below 60. */
function daysAtRisk(history: Health['history']): number {
  let n = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    const s = history[i]!.score;
    if (s === null || s >= 60) break;
    n++;
  }
  return n;
}

export function AmClientHealth({ clientId, onChanged }: { clientId: string; onChanged: () => void }) {
  const resource = useResource<Health>(`/am/clients/${clientId}/health`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overriding, setOverriding] = useState(false);

  if (resource.loading && !resource.data) return <Loading label="Reading the health score" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  const data = resource.data;
  if (!data) return null;

  const act = async (run: () => Promise<Health>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await run();
      resource.set(() => next);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the health score');
    } finally {
      setBusy(false);
    }
  };
  const recalculate = () => act(() => api.post<Health>(`/am/clients/${clientId}/health/recalculate`));
  const clearOverride = () => act(async () => {
    await api.del(`/am/clients/${clientId}/health/override`);
    return api.get<Health>(`/am/clients/${clientId}/health`);
  });

  const band = healthBand(data.score);
  const results = new Map((data.breakdown?.measures ?? []).map((m) => [m.key, m]));
  const groupScore = new Map((data.breakdown?.groups ?? []).map((g) => [g.key, g.score]));
  const atRisk = daysAtRisk(data.history);

  return (
    <div className="stack">
      {error && <ErrorBanner message={error} />}

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Client health</span>
            <h2>
              {data.score ?? '—'} <span className={`badge ${band.badge}`}>{band.label}</span>
            </h2>
            <p className="sub">
              {data.override
                ? `Overridden by ${data.override.by ?? 'someone'} until ${formatDate(data.override.until)}. The calculated score is ${data.computed ?? 'not available'}.`
                : data.computedUtc
                  ? `Calculated ${formatDate(data.computedUtc)} from ${data.breakdown?.measured ?? 0} of ${data.measures.length} measures. Recalculated every night.`
                  : 'Not calculated yet. It is worked out every night, or now with Recalculate.'}
              {atRisk > 1 && ` At risk for ${atRisk} days running.`}
            </p>
          </div>
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <Sparkline values={data.history.map((h) => h.score)} width={140} height={32} target={60} />
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void recalculate()}>
              {busy ? 'Working…' : 'Recalculate'}
            </button>
            {data.override ? (
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void clearOverride()}>
                Clear override
              </button>
            ) : (
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setOverriding(true)}>
                Override
              </button>
            )}
          </div>
        </div>
        {data.override?.reason && (
          <div className="card-body">
            <p className="small">
              <strong>Why it is overridden:</strong> {data.override.reason}
            </p>
          </div>
        )}
        {data.breakdown && data.breakdown.computed === null && (
          <div className="card-body">
            <p className="small muted">
              Fewer than three measures had anything to judge, so no score was calculated and the
              figure shown is the one that was there before.
            </p>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <h3>What it is made of</h3>
            <p className="sub">
              Healthy is 80 and above, needs attention 60 to 79, at risk below 60. A measure with
              nothing to judge carries no weight; the others take its share.
            </p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Measure</th>
                <th>Figure</th>
                <th className="num">Score</th>
                <th className="num">Weight</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <Fragment key={g.key}>
                  <tr className="am-health-group">
                    <th colSpan={2}>
                      {g.label} <span className="xs muted">{g.weight}% of the score</span>
                    </th>
                    <th className="num">
                      <ScoreBadge score={groupScore.get(g.key) ?? null} />
                    </th>
                    <th colSpan={2} />
                  </tr>
                  {data.measures
                    .filter((m) => m.group === g.key)
                    .map((m) => {
                      const r = results.get(m.key);
                      return (
                        <tr key={m.key} className={r?.score === null || !r ? 'muted' : ''}>
                          <td>
                            <div>{m.label}</div>
                            <div className="xs muted">{m.how}</div>
                          </td>
                          <td>{r?.display ?? '—'}</td>
                          <td className="num">{r && r.score !== null ? <ScoreBadge score={r.score} /> : '—'}</td>
                          <td className="num">{r && r.score !== null ? `${r.weight}%` : '—'}</td>
                          <td>
                            <div className="small">{r?.why ?? 'Not calculated yet.'}</div>
                            <div className="xs muted">{m.source}</div>
                          </td>
                        </tr>
                      );
                    })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {!!data.breakdown?.unavailable.length && (
          <div className="card-body">
            <p className="xs muted">Could not be read on the last run: {data.breakdown.unavailable.join(' · ')}</p>
          </div>
        )}
      </section>

      {overriding && (
        <OverrideForm
          clientId={clientId}
          days={data.overrideDays}
          initial={data.score}
          onClose={() => setOverriding(false)}
          onSaved={(next) => {
            resource.set(() => next);
            setOverriding(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function OverrideForm({
  clientId,
  days,
  initial,
  onClose,
  onSaved,
}: {
  clientId: string;
  days: number;
  initial: number | null;
  onClose: () => void;
  onSaved: (next: Health) => void;
}) {
  const [score, setScore] = useState(String(initial ?? ''));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = Number(score);
  const valid = score.trim() !== '' && Number.isInteger(value) && value >= 0 && value <= 100 && reason.trim().length >= 3;

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      onSaved(await api.put<Health>(`/am/clients/${clientId}/health/override`, { score: value, reason: reason.trim() }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Override the health score"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving || !valid} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <p className="small muted">
        For what the data cannot see — a champion leaving, a dispute. It lapses in {days} days and the
        calculated score comes back.
      </p>
      <div className="form-grid">
        <Field label="Score" hint="0 to 100.">
          <input className="input" type="number" min={0} max={100} value={score} onChange={(e) => setScore(e.target.value)} />
        </Field>
        <Field label="Reason">
          <textarea className="textarea" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
